/**
 * Community Events posts (Firestore: eventPosts, chat in eventPosts/{id}/messages).
 */
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getDoc,
  runTransaction,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export const EVENT_POSTS_COLLECTION = 'eventPosts';
export const EVENT_MESSAGES_SUBCOLLECTION = 'messages';
export const MAX_EVENT_WEBSITES = 10;

/** Name shown for a user on posts and chat. */
export function eventDisplayName(userData) {
  return userData?.username
    || [userData?.firstName, userData?.lastName].filter(Boolean).join(' ')
    || userData?.email?.split('@')[0]
    || 'User';
}

export function isVideoMime(mimeType, fileName = '') {
  return String(mimeType || '').startsWith('video/') || /\.(mp4|webm|mov|avi|m4v)$/i.test(fileName);
}

/**
 * Upload a picked file ({ uri, name, mimeType } from DocumentPicker, or a web File).
 * Returns { url, name, mimeType, storagePath }.
 */
export async function uploadEventFile(file, uid, folder) {
  const name = file?.name || `file-${Date.now()}`;
  const blob = file?.uri ? await (await fetch(file.uri)).blob() : file;
  const safeName = name.replace(/[^\w.-]+/g, '_');
  const storagePath = `eventPosts/${uid}/${folder}/${Date.now()}_${safeName}`;
  const fileRef = ref(getStorage(), storagePath);
  await uploadBytes(fileRef, blob);
  const url = await getDownloadURL(fileRef);
  return { url, name, mimeType: file?.mimeType || file?.type || blob?.type || '', storagePath };
}

export async function createEventPost(payload) {
  return addDoc(collection(getFirestore(), EVENT_POSTS_COLLECTION), {
    ...payload,
    createdAt: serverTimestamp(),
  });
}

export async function updateEventPost(eventId, payload) {
  return updateDoc(doc(getFirestore(), EVENT_POSTS_COLLECTION, eventId), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
}

export async function getEventChatIdentity(eventId, userId) {
  const identityRef = doc(
    getFirestore(),
    EVENT_POSTS_COLLECTION,
    eventId,
    'participants',
    userId,
  );
  const snapshot = await getDoc(identityRef);
  return snapshot.exists() ? snapshot.data().alias || '' : '';
}

export async function setEventChatIdentity(eventId, userId, alias) {
  const db = getFirestore();
  const identityRef = doc(db, EVENT_POSTS_COLLECTION, eventId, 'participants', userId);
  return runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(identityRef);
    if (snapshot.exists()) return snapshot.data().alias || '';
    transaction.set(identityRef, { alias: alias.trim(), createdAt: serverTimestamp() });
    return alias.trim();
  });
}

export async function setEventTalkEnabled(eventId, enabled) {
  return updateDoc(doc(getFirestore(), EVENT_POSTS_COLLECTION, eventId), {
    talkEnabled: enabled,
    updatedAt: serverTimestamp(),
  });
}

async function removeEventMedia(mediaPaths) {
  const uniquePaths = [...new Set(mediaPaths.filter(Boolean))];
  const cleanupResults = await Promise.allSettled(
    uniquePaths.map((path) => deleteObject(ref(getStorage(), path)))
  );
  return cleanupResults.filter((result) => result.status === 'rejected');
}

export async function deleteReplacedEventMedia(event, poster, organizerPhoto) {
  const retainedPaths = new Set([poster?.storagePath, organizerPhoto?.storagePath].filter(Boolean));
  const oldPaths = [event.poster?.storagePath, event.organizer?.photo?.storagePath]
    .filter((path) => path && !retainedPaths.has(path));
  return removeEventMedia(oldPaths);
}

export async function deleteEventPost(event) {
  const db = getFirestore();
  const [messages, participants] = await Promise.all([
    getDocs(collection(db, EVENT_POSTS_COLLECTION, event.id, EVENT_MESSAGES_SUBCOLLECTION)),
    getDocs(collection(db, EVENT_POSTS_COLLECTION, event.id, 'participants')),
  ]);
  const relatedDocs = [...messages.docs, ...participants.docs];
  for (let start = 0; start < relatedDocs.length; start += 500) {
    const batch = writeBatch(db);
    relatedDocs.slice(start, start + 500).forEach((relatedDoc) => batch.delete(relatedDoc.ref));
    await batch.commit();
  }
  await deleteDoc(doc(db, EVENT_POSTS_COLLECTION, event.id));

  return removeEventMedia([event.poster?.storagePath, event.organizer?.photo?.storagePath]);
}

export function subscribeEventPosts(onChange, onError) {
  const q = query(collection(getFirestore(), EVENT_POSTS_COLLECTION), orderBy('createdAt', 'desc'), limit(200));
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );
}

export function subscribeEventMessages(eventId, onChange, onError) {
  const q = query(
    collection(getFirestore(), EVENT_POSTS_COLLECTION, eventId, EVENT_MESSAGES_SUBCOLLECTION),
    orderBy('createdAt', 'asc'),
    limit(200),
  );
  return onSnapshot(
    q,
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError,
  );
}

export async function sendEventMessage(eventId, { userId, alias, text }) {
  return addDoc(
    collection(getFirestore(), EVENT_POSTS_COLLECTION, eventId, EVENT_MESSAGES_SUBCOLLECTION),
    {
      userId,
      alias,
      text: text.trim(),
      createdAt: serverTimestamp(),
    },
  );
}
